import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CATEGORIES = ["STOCK", "NON_STOCK", "SERVICE"];
const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];

export default function Items() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/items");
      setItems(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (item) => {
    try {
      await api.patch(`/inventory/items/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      <PageHeader title="Items" description="The product/material master shared by Procurement (PO lines) and Inventory (stock)." action={canWrite && <CreateItemDialog onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && items.length === 0 ? (
          <EmptyState title="No items yet" testId="items-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Reorder level</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`item-row-${it.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{it.code}</TableCell>
                  <TableCell className="text-foreground">{it.name}</TableCell>
                  <TableCell className="text-muted-foreground">{it.category}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{it.reorder_level ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={it.is_active} disabled={!canWrite} onCheckedChange={() => toggleActive(it)} data-testid={`item-active-toggle-${it.code}`} />
                      <Badge variant={it.is_active ? "default" : "secondary"}>{it.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateItemDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", category: "STOCK", reorder_level: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/items", { ...form, reorder_level: form.reorder_level || null });
      toast.success(`Item ${form.code} created`);
      setForm({ code: "", name: "", category: "STOCK", reorder_level: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-item-button"><Plus className="h-4 w-4" /> New Item</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Item</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-item-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required data-testid="item-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /></div>
            <div><Label>Name</Label><Input required data-testid="item-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="item-category-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reorder level</Label><Input type="number" step="0.001" data-testid="item-reorder-input" value={form.reorder_level} onChange={set("reorder_level")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-item-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
